/**
 * Trigger + poll for the AI-suggested interviewer on the ATS backend.
 *
 * Endpoints:
 *   POST /ats_interviews/trigger_suggested_interviewer → { fetch_uuid }
 *   GET  /ats_interviews/suggested_interviewer?fetch_uuid=… → { status, results }
 *
 * Kept framework-free so React Query can wrap it without leaking
 * implementation details (matches the pattern in
 * `src/api/meetingRoomCalendar/fetchMeetingRoomBusyEvents.ts`).
 */
import { api } from '@/api/request';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

export interface SuggestedInterviewer {
  id: number | string;
  name?: string;
  avatar?: string;
  email?: string;
  time_zone?: string;
}

export interface FetchSuggestedInterviewerArgs {
  atsJobStageActionId: number;
  /** ISO slot start — required by the backend; it parses with Time.iso8601. */
  startAt: string;
  /** ISO slot end — required. Backend rejects startAt >= endAt. */
  endAt: string;
  /** Whether the caller has a real slot (false uses plan duration instead). */
  isTimeslot?: boolean;
  /** IANA tz for the request; backend defaults to current user's tz if omitted. */
  timeZone?: string;
  signal?: AbortSignal;
}

interface PollPayload {
  status?: 'PENDING' | 'SUCCESS' | 'FAILED';
  results?: {
    suggested_interviewer?: SuggestedInterviewer | null;
  };
}

export async function fetchSuggestedInterviewer(
  args: FetchSuggestedInterviewerArgs
): Promise<SuggestedInterviewer | null> {
  // `start_at` / `end_at` are the preferred param names; the backend
  // still accepts `interview_start` / `interview_end` for legacy callers
  // but the comment in `trigger_suggested_interviewer_params` says it
  // will be removed post-revamp. Stick to the new names.
  const body: Record<string, unknown> = {
    ats_job_stage_action_id: args.atsJobStageActionId,
    start_at: args.startAt,
    end_at: args.endAt,
    is_timeslot: args.isTimeslot ?? true,
  };
  if (args.timeZone) body.time_zone = args.timeZone;

  const trigger = await api.post<{ fetch_uuid?: string }>(
    'ats_interviews/trigger_suggested_interviewer',
    body,
    { signal: args.signal }
  );
  const fetchUuid = trigger.fetch_uuid;
  if (!fetchUuid) {
    throw new Error('suggested_interviewer_trigger_missing_fetch_uuid');
  }

  for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
    if (args.signal?.aborted) {
      throw new DOMException('aborted', 'AbortError');
    }
    const poll = await api.get<PollPayload>(
      'ats_interviews/suggested_interviewer',
      { params: { fetch_uuid: fetchUuid }, signal: args.signal }
    );
    if (poll.status === 'SUCCESS') {
      return poll.results?.suggested_interviewer ?? null;
    }
    if (poll.status === 'FAILED') {
      throw new Error('suggested_interviewer_fetch_failed');
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
